/**
 * Session context.
 *
 * Holds the signed-in user and exposes a `may` helper backed by the shared
 * permission matrix. This drives what the UI renders; the API enforces the
 * same matrix independently, so a tampered client gains nothing.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { can, type Permission, type Role } from '@accounting/shared'
import { apiFetch, setAccessToken, setUnauthorizedHandler } from './api'

export interface SessionUser {
  id: string
  fullName: string
  email: string
  role: Role
}

/**
 * `restoring` covers the one round-trip on boot that asks the API whether the
 * session cookie still names a valid session. Nothing may decide a visitor is
 * signed out until it settles — that judgement is what used to bounce everyone
 * to the sign-in screen on reload.
 */
export type SessionStatus = 'restoring' | 'ready'

interface SessionValue {
  user: SessionUser | null
  status: SessionStatus
  may: (permission: Permission) => boolean
  signIn: (user: SessionUser, token: string) => void
  signOut: () => void
  /** Re-reads the account from the API. Call after anything that changes it. */
  refresh: () => Promise<void>
}

/** What `POST /auth/refresh` returns. The same shape sign-in answers with. */
interface RestoredSession {
  token: string
  user: SessionUser
}

const SessionContext = createContext<SessionValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [status, setStatus] = useState<SessionStatus>('restoring')

  const signOut = useCallback(() => {
    setAccessToken(null)
    setUser(null)
  }, [])

  /**
   * A token the server no longer accepts ends the session here rather than
   * leaving every panel showing an error. Registered once, in an effect, so
   * the client module never holds a handler belonging to an unmounted tree.
   */
  useEffect(() => {
    setUnauthorizedHandler(signOut)
    return () => setUnauthorizedHandler(null)
  }, [signOut])

  /**
   * Restores the session on boot (KAN-104).
   *
   * The token lives in memory and so is gone after a reload, but the httpOnly
   * cookie the API set alongside it is not. This asks for the token back. A
   * refusal is the ordinary case for anyone who is not signed in, so it is
   * swallowed: `status` moving to `ready` with no user is how "signed out" is
   * expressed, and an error banner on the sign-in screen would be noise.
   *
   * `apiFetch` does not treat the 401 as an expired session, because no token
   * was sent — see the guard around its unauthorized handler.
   */
  useEffect(() => {
    let cancelled = false

    apiFetch<RestoredSession>('/auth/refresh', { method: 'POST' })
      .then((restored) => {
        if (cancelled) return
        setAccessToken(restored.token)
        setUser(restored.user)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setStatus('ready')
      })

    return () => {
      cancelled = true
    }
  }, [])

  const value = useMemo<SessionValue>(
    () => ({
      user,
      status,
      may: (permission) => (user ? can(user.role, permission) : false),
      signIn: (nextUser, token) => {
        setAccessToken(token)
        setUser(nextUser)
      },
      signOut,
      refresh: async () => {
        const fresh = await apiFetch<SessionUser>('/auth/me')
        setUser(fresh)
      },
    }),
    [user, status, signOut]
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const context = useContext(SessionContext)
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider')
  }
  return context
}
